import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, Loader2, LayoutGrid, LayoutList, ArrowUpDown, ChevronUp, CheckSquare, X, Share2, ArrowUp, ArrowDown, GlassWater, MapPin } from 'lucide-react';
import { AppIcon } from '@/components/icons/AppIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { useWines } from '@/contexts/WinesContext';
import { useAuth } from '@/contexts/AuthContext';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { ShareBadge } from '@/components/wine/ShareBadge';
import { SelectConnectedUsersDialog } from '@/components/wine/SelectConnectedUsersDialog';
import { BulkLocationAssignmentDialog } from '@/components/wine/BulkLocationAssignmentDialog';
import { Select } from '@/components/ui/Select';
import type { Wine, WineType, WineStatus, WineSortField, WineSortDirection, WineOwnershipFilter } from '@/types';

type LayoutView = 'horizontal' | 'vertical';

const sortFieldOptions: { value: WineSortField; label: string }[] = [
  { value: 'name', label: 'Nome' },
  { value: 'date', label: 'Data Aggiunta' },
  { value: 'vintage', label: 'Annata' },
  { value: 'alcohol', label: 'Gradazione' },
  { value: 'rating', label: 'Voto' },
  { value: 'estimatedValue', label: 'Valore Stimato' },
];

const wineTypeLabels: Record<WineType, string> = {
  red: 'Rosso',
  white: 'Bianco',
  rose: 'Rosato',
  sparkling: 'Spumante',
  dessert: 'Dessert',
  fortified: 'Fortificato',
  orange: 'Arancione',
  whisky: 'Whisky',
  rum: 'Rum',
  gin: 'Gin',
  grappa: 'Grappa',
  cognac: 'Cognac',
  liqueur: 'Liquore',
  other: 'Altro',
};

const wineTypeColors: Record<WineType, string> = {
  // Vini - Palette naturale e sofisticata con sfondo pieno
  red: 'bg-wine-burgundy text-white border border-wine-burgundy',
  white: 'bg-amber-100 text-amber-900 border border-amber-300',
  rose: 'bg-wine-rose text-white border border-wine-rose',
  sparkling: 'bg-sky-100 text-sky-900 border border-sky-300',
  dessert: 'bg-amber-200 text-amber-950 border border-amber-400',
  fortified: 'bg-wine-merlot text-white border border-wine-merlot',
  orange: 'bg-orange-100 text-orange-900 border border-orange-300',

  // Spirits - Palette terra e legno con sfondo pieno
  whisky: 'bg-wine-amber text-wine-barrel border border-wine-oak',
  rum: 'bg-amber-200 text-amber-950 border border-amber-400',
  gin: 'bg-emerald-100 text-emerald-900 border border-emerald-300',
  grappa: 'bg-stone-200 text-stone-900 border border-stone-400',
  cognac: 'bg-wine-oak text-white border border-wine-barrel',
  liqueur: 'bg-fuchsia-100 text-fuchsia-900 border border-fuchsia-300',

  other: 'bg-gray-200 text-gray-900 border border-gray-400',
};

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { wines, loading, filterWines, locations } = useWines();

  // Ripristina filtri da sessionStorage
  const [searchTerm, setSearchTerm] = useState<string>(() => {
    return sessionStorage.getItem('wineSearchTerm') || '';
  });
  const [selectedStatus, setSelectedStatus] = useState<WineStatus | 'all'>(() => {
    return (sessionStorage.getItem('wineSelectedStatus') as WineStatus | 'all') || 'all';
  });
  const [selectedOwnership, setSelectedOwnership] = useState<WineOwnershipFilter>(() => {
    return (sessionStorage.getItem('wineSelectedOwnership') as WineOwnershipFilter) || 'all';
  });

  // Checkbox states for filters - ripristina da sessionStorage
  const [selectedTypes, setSelectedTypes] = useState<WineType[]>(() => {
    const saved = sessionStorage.getItem('wineSelectedTypes');
    return saved ? JSON.parse(saved) : [];
  });
  const [cellarChecked, setCellarChecked] = useState<boolean>(() => {
    return sessionStorage.getItem('wineCellarChecked') === 'true';
  });
  const [consumedChecked, setConsumedChecked] = useState<boolean>(() => {
    return sessionStorage.getItem('wineConsumedChecked') === 'true';
  });
  const [mineOnlyChecked, setMineOnlyChecked] = useState<boolean>(() => {
    return sessionStorage.getItem('wineMineOnlyChecked') === 'true';
  });
  const [mineSharedFullChecked, setMineSharedFullChecked] = useState<boolean>(() => {
    return sessionStorage.getItem('wineMineSharedFullChecked') === 'true';
  });
  const [mineSharedReadOnlyChecked, setMineSharedReadOnlyChecked] = useState<boolean>(() => {
    return sessionStorage.getItem('wineMineSharedReadOnlyChecked') === 'true';
  });
  const [sharedWithMeChecked, setSharedWithMeChecked] = useState<boolean>(() => {
    return sessionStorage.getItem('wineSharedWithMeChecked') === 'true';
  });
  const [expiredChecked, setExpiredChecked] = useState<boolean>(() => {
    return sessionStorage.getItem('wineExpiredChecked') === 'true';
  });
  const [expiringThisYearChecked, setExpiringThisYearChecked] = useState<boolean>(() => {
    return sessionStorage.getItem('wineExpiringThisYearChecked') === 'true';
  });
  const [expiringNextYearChecked, setExpiringNextYearChecked] = useState<boolean>(() => {
    return sessionStorage.getItem('wineExpiringNextYearChecked') === 'true';
  });
  const [selectedLocationId, setSelectedLocationId] = useState<string>(() => {
    return sessionStorage.getItem('wineSelectedLocationId') || '';
  });

  // Handle wine type checkbox toggle
  const toggleWineType = (type: WineType) => {
    setSelectedTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };
  const [showFilters, setShowFilters] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedWineIds, setSelectedWineIds] = useState<string[]>([]);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showBulkLocationDialog, setShowBulkLocationDialog] = useState(false);

  // Banner drag states (solo mobile < 768px)
  // Inizializza il banner in basso (80px dal fondo)
  const [bannerPosition, setBannerPosition] = useState({
    x: 0,
    y: typeof window !== 'undefined' ? window.innerHeight - 200 : 0
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const bannerRef = useRef<HTMLDivElement>(null);
  const [layoutView, setLayoutView] = useState<LayoutView>(() => {
    const saved = localStorage.getItem('wineLayoutView');
    return (saved as LayoutView) || 'horizontal';
  });
  const [sortField, setSortField] = useState<WineSortField>(() => {
    const saved = localStorage.getItem('wineSortField');
    return (saved as WineSortField) || 'date';
  });
  const [sortDirection, setSortDirection] = useState<WineSortDirection>(() => {
    const saved = localStorage.getItem('wineSortDirection');
    return (saved as WineSortDirection) || 'desc';
  });

  // Pagination state
  const [visibleCount, setVisibleCount] = useState(30);

  // Reset pagination when filters change
  useEffect(() => {
    setVisibleCount(30);
  }, [searchTerm, selectedStatus, selectedOwnership, selectedTypes, sortField, sortDirection]);

  // Save preferences
  useEffect(() => {
    localStorage.setItem('wineLayoutView', layoutView);
  }, [layoutView]);

  useEffect(() => {
    localStorage.setItem('wineSortField', sortField);
  }, [sortField]);

  useEffect(() => {
    localStorage.setItem('wineSortDirection', sortDirection);
  }, [sortDirection]);

  // Save filters to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('wineSearchTerm', searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    sessionStorage.setItem('wineSelectedStatus', selectedStatus);
  }, [selectedStatus]);

  useEffect(() => {
    sessionStorage.setItem('wineSelectedOwnership', selectedOwnership);
  }, [selectedOwnership]);

  useEffect(() => {
    sessionStorage.setItem('wineSelectedTypes', JSON.stringify(selectedTypes));
  }, [selectedTypes]);

  useEffect(() => {
    sessionStorage.setItem('wineCellarChecked', String(cellarChecked));
  }, [cellarChecked]);

  useEffect(() => {
    sessionStorage.setItem('wineConsumedChecked', String(consumedChecked));
  }, [consumedChecked]);

  useEffect(() => {
    sessionStorage.setItem('wineMineOnlyChecked', String(mineOnlyChecked));
  }, [mineOnlyChecked]);

  useEffect(() => {
    sessionStorage.setItem('wineMineSharedFullChecked', String(mineSharedFullChecked));
  }, [mineSharedFullChecked]);

  useEffect(() => {
    sessionStorage.setItem('wineMineSharedReadOnlyChecked', String(mineSharedReadOnlyChecked));
  }, [mineSharedReadOnlyChecked]);

  useEffect(() => {
    sessionStorage.setItem('wineSharedWithMeChecked', String(sharedWithMeChecked));
  }, [sharedWithMeChecked]);

  useEffect(() => {
    sessionStorage.setItem('wineExpiredChecked', String(expiredChecked));
  }, [expiredChecked]);

  useEffect(() => {
    sessionStorage.setItem('wineExpiringThisYearChecked', String(expiringThisYearChecked));
  }, [expiringThisYearChecked]);

  useEffect(() => {
    sessionStorage.setItem('wineExpiringNextYearChecked', String(expiringNextYearChecked));
  }, [expiringNextYearChecked]);

  useEffect(() => {
    sessionStorage.setItem('wineSelectedLocationId', selectedLocationId);
  }, [selectedLocationId]);

  // Scroll to top button visibility
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Verifica se ci sono filtri attivi
  const hasActiveFilters = () => {
    return (
      searchTerm.length > 0 ||
      selectedTypes.length > 0 ||
      cellarChecked ||
      consumedChecked ||
      mineOnlyChecked ||
      mineSharedFullChecked ||
      mineSharedReadOnlyChecked ||
      sharedWithMeChecked ||
      expiredChecked ||
      expiringThisYearChecked ||
      expiringNextYearChecked ||
      selectedLocationId.length > 0
    );
  };

  // Azzera tutti i filtri
  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedTypes([]);
    setCellarChecked(false);
    setConsumedChecked(false);
    setMineOnlyChecked(false);
    setMineSharedFullChecked(false);
    setMineSharedReadOnlyChecked(false);
    setSharedWithMeChecked(false);
    setExpiredChecked(false);
    setExpiringThisYearChecked(false);
    setExpiringNextYearChecked(false);
    setSelectedLocationId('');
    setSelectedStatus('all');
    setSelectedOwnership('all');
  };

  // Update selectedStatus based on checkboxes
  React.useEffect(() => {
    if (cellarChecked && consumedChecked) {
      setSelectedStatus('all');
    } else if (cellarChecked && !consumedChecked) {
      setSelectedStatus('cellar');
    } else if (!cellarChecked && consumedChecked) {
      setSelectedStatus('consumed');
    } else {
      setSelectedStatus('all'); // Default to all if none selected
    }
  }, [cellarChecked, consumedChecked]);

  // Update selectedOwnership based on checkboxes
  React.useEffect(() => {
    const anyChecked = mineOnlyChecked || mineSharedFullChecked || mineSharedReadOnlyChecked || sharedWithMeChecked;

    if (!anyChecked) {
      setSelectedOwnership('all');
    } else if (mineOnlyChecked && !mineSharedFullChecked && !mineSharedReadOnlyChecked && !sharedWithMeChecked) {
      setSelectedOwnership('mine-only');
    } else if (!mineOnlyChecked && mineSharedFullChecked && !mineSharedReadOnlyChecked && !sharedWithMeChecked) {
      setSelectedOwnership('mine-shared-full');
    } else if (!mineOnlyChecked && !mineSharedFullChecked && mineSharedReadOnlyChecked && !sharedWithMeChecked) {
      setSelectedOwnership('mine-shared-read-only');
    } else if (!mineOnlyChecked && !mineSharedFullChecked && !mineSharedReadOnlyChecked && sharedWithMeChecked) {
      setSelectedOwnership('shared-with-me');
    } else {
      setSelectedOwnership('all'); // Multiple selected = all
    }
  }, [mineOnlyChecked, mineSharedFullChecked, mineSharedReadOnlyChecked, sharedWithMeChecked]);

  // Selection Mode Handlers
  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedWineIds([]);
  };

  const toggleWineSelection = (wineId: string) => {
    // Non permettere la selezione di vini bevuti
    const wine = wines.find(w => w.id === wineId);
    if (wine?.status === 'consumed') return;

    setSelectedWineIds(prev =>
      prev.includes(wineId)
        ? prev.filter(id => id !== wineId)
        : [...prev, wineId]
    );
  };

  const selectAllWines = () => {
    // Seleziona solo vini in cantina, escludendo quelli bevuti
    setSelectedWineIds(filteredWines.filter(w => w.status === 'cellar').map(w => w.id));
  };

  const deselectAllWines = () => {
    setSelectedWineIds([]);
  };

  // Drag handlers per il banner (solo mobile < 768px)
  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    // Solo su mobile (< 768px)
    if (window.innerWidth >= 768) return;

    setIsDragging(true);
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    setDragStart({ x: clientX - bannerPosition.x, y: clientY - bannerPosition.y });
  };

  const handleDragMove = (e: MouseEvent | TouchEvent) => {
    if (!isDragging || window.innerWidth >= 768) return;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    const newX = clientX - dragStart.x;
    const newY = clientY - dragStart.y;

    // Limiti dello schermo
    const maxX = window.innerWidth - (bannerRef.current?.offsetWidth || 0);
    const maxY = window.innerHeight - (bannerRef.current?.offsetHeight || 0);

    setBannerPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Aggiungi/rimuovi event listeners per il drag
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove);
      window.addEventListener('touchend', handleDragEnd);

      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
        window.removeEventListener('touchmove', handleDragMove);
        window.removeEventListener('touchend', handleDragEnd);
      };
    }
  }, [isDragging, dragStart, bannerPosition]);

  // Filtra e ordina vini
  const filteredWines = useMemo(() => {
    let results = filterWines({
      search: searchTerm,
      type: 'all',
      status: selectedStatus,
      ownership: selectedOwnership,
      sortField: sortField,
      sortDirection: sortDirection,
      expired: expiredChecked,
      expiringThisYear: expiringThisYearChecked,
      expiringNextYear: expiringNextYearChecked,
      locationId: selectedLocationId,
    });

    // Additional filter for multiple wine types
    if (selectedTypes.length > 0) {
      results = results.filter(wine => selectedTypes.includes(wine.type));
    }

    return results;
  }, [wines, searchTerm, selectedStatus, selectedOwnership, selectedTypes, sortField, sortDirection, expiredChecked, expiringThisYearChecked, expiringNextYearChecked, selectedLocationId, filterWines]);

  // Pagination
  const displayedWines = filteredWines.slice(0, visibleCount);
  const hasMore = visibleCount < filteredWines.length;
  const loadMore = () => {
    setVisibleCount(prev => prev + 30);
  };

  // Conteggi dinamici basati sui risultati filtrati
  const totalWines = wines.length;
  const filteredCount = filteredWines.length;
  const filteredCellarCount = filteredWines.filter((w) => w.status === 'cellar').length;
  const filteredConsumedCount = filteredWines.filter((w) => w.status === 'consumed').length;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-wine-burgundy" />
        </div>
      </Layout>
    );
  }

  return (
    <>
      <Layout>
        <div className="space-y-4 sm:space-y-6">
        {/* Search & Filters */}
        <div className="space-y-3">
          {/* First row: Search + Filters (+ View/Sort on desktop) */}
          <div className="flex gap-2 sm:gap-3">
            <div className="flex-1">
              <Input
                icon={Search}
                placeholder="Cerca vino, regione, abbinamenti..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="placeholder:text-sm sm:placeholder:text-base md:h-auto h-[40px]"
                showClear={hasActiveFilters()}
                onClear={clearAllFilters}
              />
            </div>
            <Button
              variant="outline"
              icon={<Filter className="w-5 h-5 md:w-5 md:h-5 w-4 h-4" />}
              onClick={() => setShowFilters(!showFilters)}
              className="md:h-auto h-[40px]"
            >
              <span className="hidden lg:inline">Filtri</span>
            </Button>
            {/* Desktop only: Select, Sort, Direction, Layout */}
            <div className="hidden md:flex gap-2">
              {/* Select Button */}
              <Button
                variant={selectionMode ? 'primary' : 'outline'}
                icon={selectionMode ? <X className="w-5 h-5" /> : <CheckSquare className="w-5 h-5" />}
                onClick={toggleSelectionMode}
                title={selectionMode ? 'Esci dalla selezione' : 'Seleziona vini'}
              >
                <span className="hidden lg:inline">{selectionMode ? 'Annulla' : 'Seleziona'}</span>
              </Button>
              {/* Sort Field Selector */}
              <div className="relative min-w-[140px]">
                <select
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value as WineSortField)}
                  className="w-full h-full px-3 pr-8 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-wine-burgundy focus:border-transparent appearance-none cursor-pointer hover:bg-gray-50 transition-colors"
                  title="Campo ordinamento"
                >
                  {sortFieldOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ArrowUpDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
              {/* Sort Direction Selector */}
              <div className="flex gap-1 border border-gray-300 rounded-lg p-1">
                <button
                  onClick={() => setSortDirection('asc')}
                  className={`p-2 rounded transition-colors ${
                    sortDirection === 'asc'
                      ? 'bg-wine-burgundy text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title="Crescente"
                >
                  <ArrowUp className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setSortDirection('desc')}
                  className={`p-2 rounded transition-colors ${
                    sortDirection === 'desc'
                      ? 'bg-wine-burgundy text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title="Decrescente"
                >
                  <ArrowDown className="w-5 h-5" />
                </button>
              </div>
              {/* Layout Toggle */}
              <div className="flex gap-1 border border-gray-300 rounded-lg p-1">
                <button
                  onClick={() => setLayoutView('horizontal')}
                  className={`p-2 rounded transition-colors ${
                    layoutView === 'horizontal'
                      ? 'bg-wine-burgundy text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title="Vista orizzontale"
                >
                  <LayoutList className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setLayoutView('vertical')}
                  className={`p-2 rounded transition-colors ${
                    layoutView === 'vertical'
                      ? 'bg-wine-burgundy text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title="Vista griglia"
                >
                  <LayoutGrid className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Second row: Stats (left) + Mobile View/Sort (right) */}
          <div className="flex flex-col min-[480px]:flex-row items-start min-[480px]:items-center justify-between gap-2">
            {/* Mobile only: Select, Sort, Direction, Layout - Shown first on mobile (<480px) */}
            <div className="flex md:hidden gap-2 w-full order-1">
              {/* Select Button */}
              <button
                onClick={toggleSelectionMode}
                className={`h-[40px] md:h-[34px] px-2 rounded-lg border transition-colors flex items-center justify-center ${
                  selectionMode
                    ? 'bg-wine-burgundy text-white border-wine-burgundy'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'
                }`}
                title={selectionMode ? 'Esci dalla selezione' : 'Seleziona vini'}
              >
                {selectionMode ? <X className="w-4 h-4" /> : <CheckSquare className="w-4 h-4" />}
              </button>
              {/* Sort Field Selector */}
              <div className="relative flex-1">
                <select
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value as WineSortField)}
                  className="w-full h-[40px] md:h-[34px] px-2 pr-6 border border-gray-300 rounded-lg bg-white text-xs focus:ring-2 focus:ring-wine-burgundy focus:border-transparent appearance-none cursor-pointer hover:bg-gray-50 transition-colors"
                  title="Campo ordinamento"
                >
                  {sortFieldOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ArrowUpDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
              </div>
              {/* Sort Direction Toggle */}
              <div className="flex gap-0.5 border border-gray-300 rounded-lg h-[40px] md:h-[34px]">
                <button
                  onClick={() => setSortDirection('asc')}
                  className={`px-2 rounded transition-colors flex items-center justify-center ${
                    sortDirection === 'asc'
                      ? 'bg-wine-burgundy text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title="Crescente"
                >
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setSortDirection('desc')}
                  className={`px-2 rounded transition-colors flex items-center justify-center ${
                    sortDirection === 'desc'
                      ? 'bg-wine-burgundy text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title="Decrescente"
                >
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
              </div>
              {/* Layout Toggle */}
              <div className="flex gap-0.5 border border-gray-300 rounded-lg h-[40px] md:h-[34px]">
                <button
                  onClick={() => setLayoutView('horizontal')}
                  className={`px-2 rounded transition-colors flex items-center justify-center ${
                    layoutView === 'horizontal'
                      ? 'bg-wine-burgundy text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title="Vista orizzontale"
                >
                  <LayoutList className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setLayoutView('vertical')}
                  className={`px-2 rounded transition-colors flex items-center justify-center ${
                    layoutView === 'vertical'
                      ? 'bg-wine-burgundy text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title="Vista griglia"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Dynamic Stats - Shown below controls on mobile (<480px) */}
            <div className="text-xs sm:text-sm text-gray-600 order-2 min-[480px]:order-2 w-full min-[480px]:w-auto">
              {filteredCount === totalWines ? (
                <>
                  {filteredCellarCount} {filteredCellarCount === 1 ? 'vino' : 'vini'} in cantina •{' '}
                  {filteredConsumedCount} bevut{filteredConsumedCount === 1 ? 'o' : 'i'}
                </>
              ) : (
                <>
                  Visualizzati: {filteredCount} di {totalWines} ({filteredCellarCount} in cantina •{' '}
                  {filteredConsumedCount} bevut{filteredConsumedCount === 1 ? 'o' : 'i'})
                </>
              )}
            </div>
          </div>

          {/* Filters Panel - Desktop (>= 768px) */}
          <AnimatePresence>
            {showFilters && (
              <>
                {/* Desktop: pannello espandibile */}
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="overflow-hidden hidden md:block"
                >
                  <Card padding="md" className="bg-wine-parchment border-wine-cork">
                  <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={{
                      hidden: { opacity: 0 },
                      visible: {
                        opacity: 1,
                        transition: {
                          staggerChildren: 0.1,
                          delayChildren: 0.1,
                        },
                      },
                    }}
                    className="space-y-5"
                  >
                    {/* Tipo Vino - Checkboxes */}
                    <motion.div
                      variants={{
                        hidden: { opacity: 0, y: -10 },
                        visible: { opacity: 1, y: 0 },
                      }}
                    >
                      <label className="block text-sm font-medium text-wine-barrel mb-3">
                        Tipo
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-4 gap-y-2">
                        {Object.entries(wineTypeLabels).map(([value, label]) => (
                          <motion.label
                            key={value}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedTypes.includes(value as WineType)}
                              onChange={() => toggleWineType(value as WineType)}
                              className="w-4 h-4 text-wine-burgundy border-wine-cork rounded focus:ring-wine-burgundy focus:ring-2 flex-shrink-0"
                            />
                            <span className="text-sm text-wine-barrel whitespace-nowrap">{label}</span>
                          </motion.label>
                        ))}
                      </div>
                    </motion.div>

                    {/* Status - Checkboxes */}
                    <motion.div
                      variants={{
                        hidden: { opacity: 0, y: -10 },
                        visible: { opacity: 1, y: 0 },
                      }}
                    >
                      <label className="block text-sm font-medium text-wine-barrel mb-3">
                        Status
                      </label>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                        <motion.label
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={cellarChecked}
                            onChange={(e) => setCellarChecked(e.target.checked)}
                            className="w-4 h-4 text-wine-burgundy border-wine-cork rounded focus:ring-wine-burgundy focus:ring-2 flex-shrink-0"
                          />
                          <span className="text-sm text-wine-barrel">In Cantina</span>
                        </motion.label>
                        <motion.label
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={consumedChecked}
                            onChange={(e) => setConsumedChecked(e.target.checked)}
                            className="w-4 h-4 text-wine-burgundy border-wine-cork rounded focus:ring-wine-burgundy focus:ring-2 flex-shrink-0"
                          />
                          <span className="text-sm text-wine-barrel">Bevuti</span>
                        </motion.label>
                      </div>
                    </motion.div>

                    {/* Proprietà - Checkboxes */}
                    <motion.div
                      variants={{
                        hidden: { opacity: 0, y: -10 },
                        visible: { opacity: 1, y: 0 },
                      }}
                    >
                      <label className="block text-sm font-medium text-wine-barrel mb-3">
                        Proprietà
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
                        <motion.label
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={mineOnlyChecked}
                            onChange={(e) => setMineOnlyChecked(e.target.checked)}
                            className="w-4 h-4 text-wine-burgundy border-wine-cork rounded focus:ring-wine-burgundy focus:ring-2 flex-shrink-0"
                          />
                          <span className="text-sm text-wine-barrel">Solo Miei</span>
                        </motion.label>
                        <motion.label
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={mineSharedFullChecked}
                            onChange={(e) => setMineSharedFullChecked(e.target.checked)}
                            className="w-4 h-4 text-wine-burgundy border-wine-cork rounded focus:ring-wine-burgundy focus:ring-2 flex-shrink-0"
                          />
                          <span className="text-sm text-wine-barrel">Miei Condivisi Completo</span>
                        </motion.label>
                        <motion.label
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={mineSharedReadOnlyChecked}
                            onChange={(e) => setMineSharedReadOnlyChecked(e.target.checked)}
                            className="w-4 h-4 text-wine-burgundy border-wine-cork rounded focus:ring-wine-burgundy focus:ring-2 flex-shrink-0"
                          />
                          <span className="text-sm text-wine-barrel">Miei Condivisi Lettura</span>
                        </motion.label>
                        <motion.label
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={sharedWithMeChecked}
                            onChange={(e) => setSharedWithMeChecked(e.target.checked)}
                            className="w-4 h-4 text-wine-burgundy border-wine-cork rounded focus:ring-wine-burgundy focus:ring-2 flex-shrink-0"
                          />
                          <span className="text-sm text-wine-barrel">Condivisi con Me</span>
                        </motion.label>
                      </div>
                    </motion.div>

                    {/* Scadenza - Checkboxes */}
                    <motion.div
                      variants={{
                        hidden: { opacity: 0, y: -10 },
                        visible: { opacity: 1, y: 0 },
                      }}
                    >
                      <label className="block text-sm font-medium text-wine-barrel mb-3">
                        Scadenza
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
                        <motion.label
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={expiredChecked}
                            onChange={(e) => setExpiredChecked(e.target.checked)}
                            className="w-4 h-4 text-wine-burgundy border-wine-cork rounded focus:ring-wine-burgundy focus:ring-2 flex-shrink-0"
                          />
                          <span className="text-sm text-wine-barrel">Scaduti</span>
                        </motion.label>
                        <motion.label
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={expiringThisYearChecked}
                            onChange={(e) => setExpiringThisYearChecked(e.target.checked)}
                            className="w-4 h-4 text-wine-burgundy border-wine-cork rounded focus:ring-wine-burgundy focus:ring-2 flex-shrink-0"
                          />
                          <span className="text-sm text-wine-barrel">In scadenza</span>
                        </motion.label>
                        <motion.label
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={expiringNextYearChecked}
                            onChange={(e) => setExpiringNextYearChecked(e.target.checked)}
                            className="w-4 h-4 text-wine-burgundy border-wine-cork rounded focus:ring-wine-burgundy focus:ring-2 flex-shrink-0"
                          />
                          <span className="text-sm text-wine-barrel">Da bere entro un anno</span>
                        </motion.label>
                      </div>
                    </motion.div>

                    {/* Locazione */}
                    <motion.div
                      variants={{
                        hidden: { opacity: 0, y: -10 },
                        visible: { opacity: 1, y: 0 },
                      }}
                    >
                      <label className="block text-sm font-medium text-wine-barrel mb-3">
                        Dove si trova
                      </label>
                      <Select
                        options={[
                          { value: '', label: 'Ovunque' },
                          { value: 'none', label: 'Da nessuna parte' },
                          ...locations.map(loc => ({
                            value: loc.id,
                            label: loc.name
                          }))
                        ]}
                        value={selectedLocationId}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedLocationId(e.target.value)}
                      />
                    </motion.div>
                  </motion.div>

                  <div className="mt-5 flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSearchTerm('');
                        setSelectedTypes([]);
                        setCellarChecked(false);
                        setConsumedChecked(false);
                        setMineOnlyChecked(false);
                        setMineSharedFullChecked(false);
                        setMineSharedReadOnlyChecked(false);
                        setSharedWithMeChecked(false);
                        setExpiredChecked(false);
                        setExpiringThisYearChecked(false);
                        setExpiringNextYearChecked(false);
                        setSelectedLocationId('');
                      }}
                    >
                      Resetta Filtri
                    </Button>
                  </div>
                </Card>
              </motion.div>

                {/* Mobile: modale full-screen */}
                <motion.div
                  initial={{ x: '100%' }}
                  animate={{ x: 0 }}
                  exit={{ x: '100%' }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="md:hidden fixed inset-0 z-[80] bg-white flex flex-col"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-wine-burgundy text-white">
                    <div>
                      <h2 className="text-xl font-bold">Filtri</h2>
                      <p className="text-sm text-white/80 mt-0.5">
                        {filteredWines.length} {filteredWines.length === 1 ? 'vino' : 'vini'}
                      </p>
                    </div>
                    <button
                      onClick={() => setShowFilters(false)}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>

                  {/* Content scrollabile */}
                  <div className="flex-1 overflow-y-auto p-6 space-y-8">
                    {/* Tipo Vino */}
                    <div>
                      <label className="block text-base font-semibold text-wine-barrel mb-4">
                        Tipo
                      </label>
                      <div className="space-y-3">
                        {Object.entries(wineTypeLabels).map(([value, label]) => (
                          <label
                            key={value}
                            className="flex items-center gap-3 cursor-pointer py-2"
                          >
                            <input
                              type="checkbox"
                              checked={selectedTypes.includes(value as WineType)}
                              onChange={() => toggleWineType(value as WineType)}
                              className="w-6 h-6 text-wine-burgundy border-wine-cork rounded focus:ring-wine-burgundy focus:ring-2 flex-shrink-0"
                            />
                            <span className="text-base text-wine-barrel">{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Status */}
                    <div>
                      <label className="block text-base font-semibold text-wine-barrel mb-4">
                        Status
                      </label>
                      <div className="space-y-3">
                        <label className="flex items-center gap-3 cursor-pointer py-2">
                          <input
                            type="checkbox"
                            checked={cellarChecked}
                            onChange={(e) => setCellarChecked(e.target.checked)}
                            className="w-6 h-6 text-wine-burgundy border-wine-cork rounded focus:ring-wine-burgundy focus:ring-2 flex-shrink-0"
                          />
                          <span className="text-base text-wine-barrel">In Cantina</span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer py-2">
                          <input
                            type="checkbox"
                            checked={consumedChecked}
                            onChange={(e) => setConsumedChecked(e.target.checked)}
                            className="w-6 h-6 text-wine-burgundy border-wine-cork rounded focus:ring-wine-burgundy focus:ring-2 flex-shrink-0"
                          />
                          <span className="text-base text-wine-barrel">Bevuti</span>
                        </label>
                      </div>
                    </div>

                    {/* Proprietà */}
                    <div>
                      <label className="block text-base font-semibold text-wine-barrel mb-4">
                        Proprietà
                      </label>
                      <div className="space-y-3">
                        <label className="flex items-center gap-3 cursor-pointer py-2">
                          <input
                            type="checkbox"
                            checked={mineOnlyChecked}
                            onChange={(e) => setMineOnlyChecked(e.target.checked)}
                            className="w-6 h-6 text-wine-burgundy border-wine-cork rounded focus:ring-wine-burgundy focus:ring-2 flex-shrink-0"
                          />
                          <span className="text-base text-wine-barrel">Solo Miei</span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer py-2">
                          <input
                            type="checkbox"
                            checked={mineSharedFullChecked}
                            onChange={(e) => setMineSharedFullChecked(e.target.checked)}
                            className="w-6 h-6 text-wine-burgundy border-wine-cork rounded focus:ring-wine-burgundy focus:ring-2 flex-shrink-0"
                          />
                          <span className="text-base text-wine-barrel">Miei Condivisi Completo</span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer py-2">
                          <input
                            type="checkbox"
                            checked={mineSharedReadOnlyChecked}
                            onChange={(e) => setMineSharedReadOnlyChecked(e.target.checked)}
                            className="w-6 h-6 text-wine-burgundy border-wine-cork rounded focus:ring-wine-burgundy focus:ring-2 flex-shrink-0"
                          />
                          <span className="text-base text-wine-barrel">Miei Condivisi Lettura</span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer py-2">
                          <input
                            type="checkbox"
                            checked={sharedWithMeChecked}
                            onChange={(e) => setSharedWithMeChecked(e.target.checked)}
                            className="w-6 h-6 text-wine-burgundy border-wine-cork rounded focus:ring-wine-burgundy focus:ring-2 flex-shrink-0"
                          />
                          <span className="text-base text-wine-barrel">Condivisi con Me</span>
                        </label>
                      </div>
                    </div>

                    {/* Scadenza */}
                    <div>
                      <label className="block text-base font-semibold text-wine-barrel mb-4">
                        Scadenza
                      </label>
                      <div className="space-y-3">
                        <label className="flex items-center gap-3 cursor-pointer py-2">
                          <input
                            type="checkbox"
                            checked={expiredChecked}
                            onChange={(e) => setExpiredChecked(e.target.checked)}
                            className="w-6 h-6 text-wine-burgundy border-wine-cork rounded focus:ring-wine-burgundy focus:ring-2 flex-shrink-0"
                          />
                          <span className="text-base text-wine-barrel">Scaduti</span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer py-2">
                          <input
                            type="checkbox"
                            checked={expiringThisYearChecked}
                            onChange={(e) => setExpiringThisYearChecked(e.target.checked)}
                            className="w-6 h-6 text-wine-burgundy border-wine-cork rounded focus:ring-wine-burgundy focus:ring-2 flex-shrink-0"
                          />
                          <span className="text-base text-wine-barrel">In scadenza</span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer py-2">
                          <input
                            type="checkbox"
                            checked={expiringNextYearChecked}
                            onChange={(e) => setExpiringNextYearChecked(e.target.checked)}
                            className="w-6 h-6 text-wine-burgundy border-wine-cork rounded focus:ring-wine-burgundy focus:ring-2 flex-shrink-0"
                          />
                          <span className="text-base text-wine-barrel">Da bere entro un anno</span>
                        </label>
                      </div>
                    </div>

                    {/* Locazione */}
                    <div>
                      <label className="block text-base font-semibold text-wine-barrel mb-4">
                        Dove si trova
                      </label>
                      <Select
                        options={[
                          { value: '', label: 'Ovunque' },
                          { value: 'none', label: 'Da nessuna parte' },
                          ...locations.map(loc => ({
                            value: loc.id,
                            label: loc.name
                          }))
                        ]}
                        value={selectedLocationId}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedLocationId(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Footer con bottoni */}
                  <div className="p-4 border-t border-gray-200 space-y-3 bg-gray-50">
                    <Button
                      variant="outline"
                      fullWidth
                      onClick={() => {
                        setSearchTerm('');
                        setSelectedTypes([]);
                        setCellarChecked(false);
                        setConsumedChecked(false);
                        setMineOnlyChecked(false);
                        setMineSharedFullChecked(false);
                        setMineSharedReadOnlyChecked(false);
                        setSharedWithMeChecked(false);
                        setExpiredChecked(false);
                        setExpiringThisYearChecked(false);
                        setExpiringNextYearChecked(false);
                        setSelectedLocationId('');
                      }}
                    >
                      Resetta Filtri
                    </Button>
                    <Button
                      variant="primary"
                      fullWidth
                      onClick={() => setShowFilters(false)}
                    >
                      Applica Filtri
                    </Button>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>

        {/* Wine Grid */}
        {filteredWines.length === 0 ? (
          <Card padding="lg">
            <div className="text-center py-12">
              <AppIcon className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {wines.length === 0 ? 'Nessun vino in cantina' : 'Nessun risultato'}
              </h3>
              <p className="text-gray-600 mb-6">
                {wines.length === 0
                  ? 'Inizia ad aggiungere i tuoi vini preferiti alla cantina digitale!'
                  : 'Prova a modificare i filtri di ricerca'}
              </p>
              {wines.length === 0 && (
                <Button
                  icon={<Plus className="w-5 h-5" />}
                  onClick={() => navigate('/add')}
                  className="mx-auto"
                >
                  Aggiungi il tuo primo vino
                </Button>
              )}
            </div>
          </Card>
        ) : (
          <motion.div
            key={`${layoutView}-${selectedStatus}-${selectedOwnership}-${selectedTypes.join(',')}`}
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.05,
                  delayChildren: 0.1,
                },
              },
            }}
            className={
              layoutView === 'horizontal'
                ? 'grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 gap-3'
                : 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 auto-rows-fr'
            }
          >
            {displayedWines.map((wine) =>
              layoutView === 'horizontal' ? (
                <motion.div
                  key={wine.id}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        type: 'spring',
                        stiffness: 100,
                        damping: 12,
                      },
                    },
                  }}
                >
                  <WineCard
                    wine={wine}
                    onClick={() => selectionMode ? toggleWineSelection(wine.id) : navigate(`/wine/${wine.id}`)}
                    userId={user?.id || ''}
                    selectionMode={selectionMode}
                    isSelected={selectedWineIds.includes(wine.id)}
                    onToggleSelection={() => toggleWineSelection(wine.id)}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key={wine.id}
                  variants={{
                    hidden: { opacity: 0, scale: 0.9 },
                    visible: {
                      opacity: 1,
                      scale: 1,
                      transition: {
                        type: 'spring',
                        stiffness: 100,
                        damping: 12,
                      },
                    },
                  }}
                >
                  <WineCardVertical
                    wine={wine}
                    onClick={() => selectionMode ? toggleWineSelection(wine.id) : navigate(`/wine/${wine.id}`)}
                    userId={user?.id || ''}
                    selectionMode={selectionMode}
                    isSelected={selectedWineIds.includes(wine.id)}
                    onToggleSelection={() => toggleWineSelection(wine.id)}
                  />
                </motion.div>
              )
            )}
          </motion.div>
        )}

        {/* Load More Button */}
        {!loading && filteredWines.length > 0 && hasMore && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center mt-8"
          >
            <Button
              variant="outline"
              size="lg"
              onClick={loadMore}
              className="min-w-[200px]"
            >
              Carica altri 30 vini
            </Button>
          </motion.div>
        )}
        </div>
      </Layout>

      {/* Scroll to Top Button */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={scrollToTop}
            className="fixed bottom-[88px] right-4 lg:bottom-10 lg:right-6 z-[60] p-2 bg-wine-burgundy text-white rounded-lg shadow-lg hover:bg-wine-burgundy/90 transition-colors"
            title="Torna su"
          >
            <ChevronUp className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Selection Toolbar */}
      <AnimatePresence>
        {selectionMode && selectedWineIds.length > 0 && !showShareDialog && !showBulkLocationDialog && (
          <motion.div
            ref={bannerRef}
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed z-[70] w-auto max-w-md"
            style={{
              // Su mobile (<768px): usa posizione draggable con top/left, su desktop: posizione fissa con bottom
              ...(window.innerWidth < 768
                ? {
                    top: `${bannerPosition.y}px`,
                    left: `${bannerPosition.x}px`,
                    marginLeft: '1rem',
                    marginRight: '1rem',
                    width: 'calc(100% - 2rem)',
                    maxWidth: 'none',
                    cursor: isDragging ? 'grabbing' : 'grab',
                  }
                : {
                    left: '0',
                    right: '0',
                    bottom: '5rem',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    width: '90%',
                    maxWidth: '28rem',
                    cursor: 'default',
                  }),
              touchAction: 'none',
            }}
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
          >
            <div className="bg-white rounded-lg shadow-2xl border-2 border-wine-burgundy p-4 md:p-3">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
                <div className="flex items-center gap-3 sm:gap-3 min-w-0 flex-1">
                  <div className="w-12 h-12 md:w-10 md:h-10 bg-wine-burgundy rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-base md:text-sm">{selectedWineIds.length}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-900 text-base md:text-sm truncate">
                      {selectedWineIds.length} {selectedWineIds.length === 1 ? 'vino selezionato' : 'vini selezionati'}
                    </p>
                    {/* Su mobile: bottoni veri, su desktop: link testuali */}
                    <div className="flex gap-2 mt-2 md:hidden">
                      <Button
                        onClick={selectAllWines}
                        variant="outline"
                        size="sm"
                        className="flex-1 h-9"
                      >
                        Tutto
                      </Button>
                      <Button
                        onClick={deselectAllWines}
                        variant="outline"
                        size="sm"
                        className="flex-1 h-9"
                      >
                        Nessuno
                      </Button>
                    </div>
                    <div className="hidden md:flex gap-2 text-xs text-gray-600 mt-0.5">
                      <button
                        onClick={selectAllWines}
                        className="hover:text-wine-burgundy transition-colors"
                      >
                        Tutto
                      </button>
                      <span>•</span>
                      <button
                        onClick={deselectAllWines}
                        className="hover:text-wine-burgundy transition-colors"
                      >
                        Nessuno
                      </button>
                    </div>
                  </div>
                </div>
                <Button
                  icon={<Share2 className="w-5 h-5" />}
                  onClick={() => setShowShareDialog(true)}
                  size="sm"
                  className="flex-shrink-0 w-full sm:w-auto h-12 md:h-auto"
                >
                  Condividi
                </Button>
                <Button
                  icon={<MapPin className="w-5 h-5" />}
                  onClick={() => setShowBulkLocationDialog(true)}
                  size="sm"
                  variant="outline"
                  className="flex-shrink-0 w-full sm:w-auto h-12 md:h-auto"
                >
                  Assegna Posizione
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Select Connected Users Dialog for Multi-Share */}
      <SelectConnectedUsersDialog
        isOpen={showShareDialog}
        onClose={() => {
          setShowShareDialog(false);
          setSelectionMode(false);
          setSelectedWineIds([]);
        }}
        wineIds={selectedWineIds}
        wineNames={wines.filter(w => selectedWineIds.includes(w.id)).map(w => w.name)}
      />

      {/* Bulk Location Assignment Dialog */}
      <BulkLocationAssignmentDialog
        isOpen={showBulkLocationDialog}
        onClose={() => {
          setShowBulkLocationDialog(false);
          setSelectionMode(false);
          setSelectedWineIds([]);
        }}
        wineIds={selectedWineIds}
        wineNames={wines.filter(w => selectedWineIds.includes(w.id)).map(w => w.name)}
      />
    </>
  );
};

interface WineCardProps {
  wine: Wine;
  onClick: () => void;
  userId: string;
  selectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelection?: () => void;
}

// Horizontal Card Component
const WineCard: React.FC<WineCardProps> = ({ wine, onClick, userId, selectionMode, isSelected, onToggleSelection }) => {
  // Usa imageUrls se disponibile, altrimenti fallback a imageUrl (retrocompatibilità)
  const firstImage = wine.imageUrls?.[0] || wine.imageUrl;

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
    >
      <Card hover onClick={onClick} padding="none" className={selectionMode && isSelected ? 'ring-2 ring-wine-burgundy' : ''}>
        <div className="flex flex-row h-[140px]">
        {/* Selection Checkbox */}
        {selectionMode && (
          <div className="flex items-center justify-center px-3">
            <motion.div
              onClick={(e) => {
                e.stopPropagation();
                onToggleSelection?.();
              }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              animate={isSelected ? { scale: [1, 1.2, 1] } : {}}
              transition={{ duration: 0.3 }}
              className={`w-6 h-6 rounded border-2 flex items-center justify-center cursor-pointer ${
                isSelected
                  ? 'bg-wine-burgundy border-wine-burgundy'
                  : 'border-gray-300 hover:border-wine-burgundy/50'
              }`}
            >
              {isSelected && (
                <motion.svg
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  className="w-4 h-4 text-white"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M5 13l4 4L19 7"></path>
                </motion.svg>
              )}
            </motion.div>
          </div>
        )}

        {/* Wine Image - 100px width, vertical orientation */}
        <div className="w-[100px] h-full flex-shrink-0 relative">
          {firstImage ? (
            <img
              src={firstImage}
              alt={wine.name}
              className="w-full h-full object-cover rounded-l-lg"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-wine-burgundy/10 to-wine-burgundy/5 rounded-l-lg flex items-center justify-center">
              <AppIcon className="w-8 h-8 opacity-30" />
            </div>
          )}

          {/* Badges overlay on image */}
          <div className="absolute inset-0 pointer-events-none">
            {/* Top-left: Consumed Icon */}
            {wine.status === 'consumed' && (
              <div className="absolute top-2 left-2">
                <div className="p-1 rounded-full bg-gray-200 shadow-sm" title="Bevuto">
                  <GlassWater className="w-3 h-3 text-gray-800" />
                </div>
              </div>
            )}

            {/* Top-right: Share Badge */}
            <div className="absolute top-2 right-2 pointer-events-auto">
              <ShareBadge wine={wine} currentUserId={userId} size="sm" iconOnly />
            </div>

            {/* Bottom Center: Rating */}
            {wine.status === 'consumed' && wine.rating && (
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-0.5 bg-black/80 px-2 py-1 rounded-md">
                {Array.from({ length: 5 }).map((_, i) => (
                  <svg
                    key={i}
                    className={`w-3 h-3 ${
                      i < wine.rating! ? 'text-yellow-400 fill-current' : 'text-gray-400'
                    }`}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Wine Info - remaining width */}
        <div className="flex-1 p-3 sm:p-4 flex flex-col justify-between min-w-0 gap-1.5">
          {/* Nome, Cantina, Anno e Regione */}
          <div className="min-w-0">
            <h3 className="text-base sm:text-lg font-serif font-semibold text-wine-barrel truncate leading-snug">
              {wine.name}
            </h3>
            <p className="text-sm text-gray-600 truncate leading-normal mt-0.5">
              {wine.producer}
            </p>
            {(wine.vintage || wine.region) && (
              <p className="text-xs text-gray-500 truncate mt-0.5">
                {wine.vintage}
                {wine.vintage && wine.region && ' • '}
                {wine.region}
              </p>
            )}
          </div>

          {/* Badge: Tipo e Gradazione a sinistra | Quantità e Prezzo a destra */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-shrink">
              {/* Wine Type Badge - sempre visibile */}
              <span className={`px-2 py-1 rounded text-xs font-semibold ${wineTypeColors[wine.type]}`}>
                {wineTypeLabels[wine.type]}
              </span>

              {/* Alcohol Content Badge - nascosto sotto 375px */}
              {wine.alcoholContent && (
                <span className="hidden min-[375px]:inline-flex px-2 py-1 rounded text-xs font-semibold text-wine-gold border border-gray-300 shadow-sm">
                  {wine.alcoholContent}%
                </span>
              )}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Quantity Badge - nascosto sotto 375px e tra 1024px-1440px */}
              {wine.status === 'cellar' && wine.quantity && wine.quantity > 1 && (
                <span className="hidden min-[375px]:inline-flex lg:hidden min-[1440px]:inline-flex px-2 py-1 rounded text-xs font-semibold bg-blue-100 text-blue-900 border border-blue-300 shadow-sm">
                  ×{wine.quantity}
                </span>
              )}

              {/* Value Badge - sempre visibile - priorità a purchasePrice */}
              {(wine.purchasePrice || wine.estimatedValue) && (
                <span className="px-2 py-1 rounded text-xs font-semibold bg-wine-gold/20 text-wine-gold border border-wine-gold/40 shadow-sm">
                  €{(wine.purchasePrice ?? wine.estimatedValue)!.toFixed(0)}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
    </motion.div>
  );
};

// Vertical Card Component (E-commerce style)
const WineCardVertical: React.FC<WineCardProps> = ({ wine, onClick, userId, selectionMode, isSelected, onToggleSelection }) => {
  const firstImage = wine.imageUrls?.[0] || wine.imageUrl;
  const badgesRef = useRef<HTMLDivElement>(null);
  const producerRef = useRef<HTMLParagraphElement>(null);
  const [isProducerOverflowing, setIsProducerOverflowing] = useState(false);

  // Check if producer text overflows
  useEffect(() => {
    if (producerRef.current) {
      const element = producerRef.current;
      const isOverflowing = element.scrollWidth > element.clientWidth;
      setIsProducerOverflowing(isOverflowing);
    }
  }, [wine.producer]);

  // GSAP animations on mount and hover effects
  useEffect(() => {
    if (badgesRef.current) {
      const badges = badgesRef.current.querySelectorAll('.badge-item');

      // Initial animation when badges appear
      gsap.fromTo(
        badges,
        {
          opacity: 0,
          scale: 0.5,
          y: -10
        },
        {
          opacity: 1,
          scale: 1,
          y: 0,
          duration: 0.4,
          stagger: 0.08,
          ease: 'back.out(1.7)',
        }
      );

      // Add hover animations to each badge
      badges.forEach((badge) => {
        const element = badge as HTMLElement;

        element.addEventListener('mouseenter', () => {
          gsap.to(element, {
            scale: 1.1,
            duration: 0.3,
            ease: 'power2.out'
          });
        });

        element.addEventListener('mouseleave', () => {
          gsap.to(element, {
            scale: 1,
            duration: 0.3,
            ease: 'power2.out'
          });
        });
      });

      // Cleanup
      return () => {
        badges.forEach((badge) => {
          const element = badge as HTMLElement;
          element.removeEventListener('mouseenter', () => {});
          element.removeEventListener('mouseleave', () => {});
        });
      };
    }
  }, [wine.id]);

  return (
    <motion.div
      whileHover={{ scale: 1.03, y: -4 }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      className="h-full"
    >
      <Card hover onClick={onClick} padding="none" className={`h-full ${selectionMode && isSelected ? 'ring-2 ring-wine-burgundy' : ''}`}>
        <div className="flex flex-col h-full">
        {/* Wine Image - top section with max height */}
        <div className="relative w-full h-[220px]" ref={badgesRef}>
          {/* Selection Checkbox - Overlay on image */}
          {selectionMode && (
            <div className="absolute top-2 right-2 z-10">
              <motion.div
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleSelection?.();
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                animate={isSelected ? { scale: [1, 1.2, 1] } : {}}
                transition={{ duration: 0.3 }}
                className={`w-7 h-7 rounded border-2 flex items-center justify-center cursor-pointer backdrop-blur-sm shadow-sm ${
                  isSelected
                    ? 'bg-wine-burgundy border-wine-burgundy'
                    : 'bg-white/95 border-gray-300 hover:border-wine-burgundy/50'
                }`}
              >
                {isSelected && (
                  <motion.svg
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    className="w-4 h-4 text-white"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M5 13l4 4L19 7"></path>
                  </motion.svg>
                )}
              </motion.div>
            </div>
          )}
          {firstImage ? (
            <img
              src={firstImage}
              alt={wine.name}
              className="w-full h-full object-cover rounded-t-lg"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-wine-burgundy/10 to-wine-burgundy/5 rounded-t-lg flex items-center justify-center">
              <AppIcon className="w-12 h-12 sm:w-16 sm:h-16 opacity-30" />
            </div>
          )}

          {/* Badges overlay on image */}
          <div className="absolute inset-0 pointer-events-none">
            {/* Top-left: Wine Type Badge */}
            <div className="absolute top-2 left-2">
              <span className={`badge-item px-2 py-1 rounded text-xs font-semibold shadow-sm ${wineTypeColors[wine.type]}`}>
                {wineTypeLabels[wine.type]}
              </span>
            </div>

            {/* Top-right: Alcohol Content (above) and Quantity (below) */}
            <div className="absolute top-2 right-2 flex flex-col gap-1.5 items-end">
              {/* Alcohol Content Badge */}
              {wine.alcoholContent && (
                <span className="badge-item px-2 py-1 rounded text-xs font-semibold text-wine-gold border border-gray-300 shadow-sm bg-white/90 backdrop-blur-sm">
                  {wine.alcoholContent}%
                </span>
              )}

              {/* Quantity Badge - nascosto tra 1024px-1440px */}
              {wine.status === 'cellar' && wine.quantity && wine.quantity > 1 && (
                <span className="badge-item px-2 py-1 rounded text-xs font-semibold bg-blue-100 text-blue-900 border border-blue-300 shadow-sm lg:hidden min-[1440px]:inline-flex">
                  ×{wine.quantity}
                </span>
              )}
            </div>

            {/* Bottom-left: Consumed Icon and Rating */}
            <div className="absolute bottom-2 left-2 flex items-center gap-2">
              {/* Consumed Icon */}
              {wine.status === 'consumed' && (
                <div className="badge-item p-1.5 rounded-full bg-gray-200 shadow-sm" title="Bevuto">
                  <GlassWater className="w-4 h-4 text-gray-800" />
                </div>
              )}

              {/* Rating */}
              {wine.status === 'consumed' && wine.rating && (
                <div className="badge-item flex items-center gap-0.5 bg-black/80 px-2 py-1 rounded-md">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <svg
                      key={i}
                      className={`w-3 h-3 sm:w-3.5 sm:h-3.5 ${
                        i < wine.rating! ? 'text-yellow-400 fill-current' : 'text-gray-400'
                      }`}
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              )}
            </div>

            {/* Bottom-right: Share Badge (icon only) */}
            <div className="absolute bottom-2 right-2 pointer-events-auto badge-item">
              <ShareBadge wine={wine} currentUserId={userId} size="sm" iconOnly />
            </div>
          </div>
        </div>

        {/* Wine Info - bottom section, centered */}
        <div className="p-4 flex flex-col items-center text-center flex-1">
          {/* Name - fixed height, aligned top */}
          <h3 className="text-base sm:text-lg font-serif font-semibold text-wine-barrel line-clamp-2 w-full h-12 sm:h-14 flex items-start justify-center leading-snug">
            {wine.name}
          </h3>

          {/* Producer - fixed height */}
          <p
            ref={producerRef}
            className={`text-sm text-gray-600 truncate w-full h-6 flex items-center mt-1 ${
              isProducerOverflowing ? 'justify-start text-left' : 'justify-center text-center'
            }`}
          >
            {wine.producer}
          </p>

          {/* Metadata - fixed height */}
          <div className="flex items-center justify-center gap-1.5 text-xs text-gray-600 h-6 mt-2 overflow-hidden w-full">
            {wine.vintage && <span className="flex-shrink-0">{wine.vintage}</span>}
            {wine.vintage && wine.region && <span className="flex-shrink-0">•</span>}
            {wine.region && <span className="truncate">{wine.region}</span>}
          </div>

          {/* Spacer to push value to bottom */}
          <div className="flex-1"></div>

          {/* Value - anchored to bottom - priorità a purchasePrice */}
          {(wine.purchasePrice || wine.estimatedValue) && (
            <div className="bg-wine-gold/10 border border-wine-gold/30 px-3 py-2 rounded-lg w-full mt-3">
              <span className="text-base font-bold text-wine-gold">
                €{(wine.purchasePrice ?? wine.estimatedValue)!.toFixed(0)}
              </span>
            </div>
          )}
        </div>
      </div>
    </Card>
    </motion.div>
  );
};
